import React from 'react';

import { useGetList, useGetSelf } from '@vlabs/api/artemy/client';
import { DuplicateIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';

import { ListFormPoint3 } from '@vlabs/monokit/entities/lists';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import type { DuplicateList } from './types';
import { useDuplicateListSubmit } from './useDuplicateListSubmit';

type DuplicateListIconButtonProps = DuplicateList & IconButtonProps;

const displayName = 'DuplicateListIconButton';
const testid = buildTestid(displayName);

function DuplicateListIconButton({
  listId,
  'data-testid': dataTestId = undefined,
  ...props
}: DuplicateListIconButtonProps): React.JSX.Element | null {
  const modal = useModal();

  const { data: list } = useGetList(listId);
  const { data: self } = useGetSelf();
  const handleSubmit = useDuplicateListSubmit({ listId, onSuccess: modal.close });

  const defaultValues = {
    user_data: list?.user_data ? `Copy_of_${list.user_data}` : '',
  };

  if (list?.owner_department && !self?.department_ids.includes(list.owner_department)) return null;

  return (
    <React.Fragment>
      {modal.wrap(
        <ListFormPoint3
          context="duplicate"
          defaultValues={defaultValues}
          duplicateSourceName={list?.user_data}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <IconButton
        icon={<DuplicateIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      />
    </React.Fragment>
  );
}

DuplicateListIconButton.displayName = displayName;

export { DuplicateListIconButton };
